import React from "react";
import { TextNode, TextNodeProps as RPDFCTextNodeProps, Float, View } from "@surge-global-engineering/react-pdf-components";
import { StyleSheet } from "@surge-global-engineering/rpdf-renderer";
import { PdfSlateTextNode, PdfTheme } from "../types";
import { getDropcapTuningConfig, VERY_VERY_SMALL_MARGIN } from "../configs";

interface DropcapProps {
  theme: PdfTheme;
}

const NEGLEGIBLE_VALUE = 0.0001;

const calculateDropcapLineHeight = (
  baseFontSize: number,
  baseFontLineHeight: number,
  dropcapFontSize: number
) => {
  return (baseFontSize * baseFontLineHeight) / dropcapFontSize;
};

const calculateBottomShift = (
  baseFontSize: number,
  baseFontLineHeight: number,
  dropcapFontSize: number,
  dropcapLineHeight: number,
  topShift: number
) => {
  const expectedTotalHeight =
    2 * (baseFontSize * baseFontLineHeight) - NEGLEGIBLE_VALUE;
  const occupiedHeight = dropcapFontSize * dropcapLineHeight + topShift;
  const availableHeight = Math.max(expectedTotalHeight - occupiedHeight, 0);

  return availableHeight;
};

const getFontVarient = (node: PdfSlateTextNode): "bold" | "italic" | "regular" | "bold-italic" => {
  const varientArray: string[] = [];
  for (const key in node) {
    if (key === "bold" && node["bold"]) {
      varientArray.push("bold");
    }
    if (key === "italic" && node["italic"]) {
      varientArray.push("italic");
    }
  }
  if (["bold", "italic"].every(varient => varientArray.includes(varient))) {
    return "bold-italic";
  }
  if (["bold"].every(varient => varientArray.includes(varient))) {
    return "bold";
  }
  if (["italic"].every(varient => varientArray.includes(varient))) {
    return "italic";
  }
  return "regular";
};

const Dropcap: React.FC<DropcapProps> = ({ theme, children }) => {
  const { baseFontSize, printBaseFont, paragraph } = theme.properties;
  const { paragraphSpacing } = paragraph;
  const {
    bold,
    italic,
    // underline,
    // monospace,
    // smallcaps,
    // subscript,
    // superscript,
    // strikethrough,
    // sansserif,
    text
  } = children as PdfSlateTextNode;

  const fontVarient = getFontVarient(children as PdfSlateTextNode);
  const dropcapTuningParams = getDropcapTuningConfig(printBaseFont, fontVarient);
  const topShift = baseFontSize * dropcapTuningParams.verticalShiftMultiplier;

  const dropcapFontSize =
    ((1 + paragraphSpacing) * baseFontSize - topShift) *
    dropcapTuningParams.sizeMultiplier;

  const dropcapLineHeight = calculateDropcapLineHeight(
    baseFontSize,
    paragraphSpacing,
    dropcapFontSize
  );

  const bottomShift = calculateBottomShift(
    baseFontSize,
    paragraphSpacing,
    dropcapFontSize,
    dropcapLineHeight,
    topShift
  );

  const props: RPDFCTextNodeProps = {
    bold: bold,
    italic: italic,
    fontSize: dropcapFontSize,
    lineHeight: paragraphSpacing,
    // underline: underline,
    // monospace: monospace,
    // smallCaps: smallcaps,
    // sansSerif: sansserif,
    // subscript: subscript,
    // superscript: superscript,
    // strikeThrough: strikethrough,
  };

  const styles = StyleSheet.create({
    text: {
      fontSize: dropcapFontSize,
      marginRight: VERY_VERY_SMALL_MARGIN,
      fontFamily: printBaseFont,
      lineHeight: dropcapLineHeight,
    },
  });

  return (
    <Float direction="left">
      <View style={{ marginRight: 3 }}>
        <View style={{ marginTop: topShift }}></View>
        <TextNode {...props} style={styles.text}>
          {text}
        </TextNode>
        <View style={{ marginBottom: bottomShift }}></View>
      </View>
    </Float>
  );
};

export default Dropcap;
